import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const SHOW_DELAY_MS = 300;
const GAP_PX = 6;
const VIEWPORT_MARGIN_PX = 8;
/** How far up from the hovered node to look for the element that clips the text. */
const MAX_ANCESTOR_DEPTH = 6;

interface TooltipState {
  text: string;
  rect: DOMRect;
}

const isClipping = (element: HTMLElement) => {
  const style = getComputedStyle(element);
  const ellipsis = style.textOverflow === 'ellipsis';
  const lineClamp = style.webkitLineClamp && style.webkitLineClamp !== 'none';
  if (!ellipsis && !lineClamp) return false;
  return (
    element.scrollWidth > element.clientWidth + 1 || element.scrollHeight > element.clientHeight + 1
  );
};

/** The nearest element above `node` whose text is cut off with an ellipsis or line clamp. */
const findClippedElement = (node: EventTarget | null): HTMLElement | null => {
  let element = node instanceof HTMLElement ? node : null;
  for (
    let depth = 0;
    element && depth < MAX_ANCESTOR_DEPTH;
    depth++, element = element.parentElement
  ) {
    if (element.closest('[data-no-overflow-tooltip]')) return null;
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) return null;
    if (isClipping(element)) return element.title ? null : element;
  }
  return null;
};

/**
 * Site-wide: hovering text that is cut off (Tailwind `truncate` / `line-clamp-*`) shows the whole text in a
 * tooltip; on a touch screen, tapping it does. Mounted once at the app root, so no component has to wrap its truncated text. Opt out with
 * `data-no-overflow-tooltip`; elements with their own `title` keep it.
 */
export const OverflowTooltip = () => {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let target: HTMLElement | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const hide = () => {
      clearTimeout(timer);
      target = null;
      setTooltip(null);
    };

    const handleOver = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      const element = findClippedElement(event.target);
      if (element === target) return;
      hide();
      if (!element) return;
      target = element;
      timer = setTimeout(() => {
        const text = element.innerText.trim();
        if (text && element.isConnected)
          setTooltip({ text, rect: element.getBoundingClientRect() });
      }, SHOW_DELAY_MS);
    };

    const handleOut = (event: PointerEvent) => {
      if (target && !target.contains(event.relatedTarget as Node | null)) hide();
    };

    // A touch screen has no hover: tapping cut-off text shows it at once, the next tap hides it.
    let lastPointerType = '';
    const handleDown = (event: PointerEvent) => {
      lastPointerType = event.pointerType;
      hide();
    };
    const handleClick = (event: MouseEvent) => {
      if (lastPointerType !== 'touch') return;
      const element = findClippedElement(event.target);
      if (!element) return;
      const text = element.innerText.trim();
      if (!text) return;
      target = element;
      setTooltip({ text, rect: element.getBoundingClientRect() });
    };

    document.addEventListener('pointerover', handleOver);
    document.addEventListener('pointerout', handleOut);
    document.addEventListener('pointerdown', handleDown, true);
    document.addEventListener('click', handleClick, true);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('blur', hide);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerover', handleOver);
      document.removeEventListener('pointerout', handleOut);
      document.removeEventListener('pointerdown', handleDown, true);
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('blur', hide);
    };
  }, []);

  // Above the text, or below it when there is no room; kept inside the viewport horizontally.
  useLayoutEffect(() => {
    if (!tooltip || !tooltipRef.current) {
      setPosition(null);
      return;
    }
    const { width, height } = tooltipRef.current.getBoundingClientRect();
    const { rect } = tooltip;
    const above = rect.top - GAP_PX - height;
    const top = above >= VIEWPORT_MARGIN_PX ? above : rect.bottom + GAP_PX;
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN_PX;
    const left = Math.max(VIEWPORT_MARGIN_PX, Math.min(rect.left, maxLeft));
    setPosition({ top, left });
  }, [tooltip]);

  if (!tooltip) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      role="tooltip"
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? 'visible' : 'hidden',
      }}
      className="pointer-events-none fixed z-tooltip max-w-sm whitespace-pre-line break-words rounded-md bg-foreground px-3 py-1.5 text-xs text-background shadow-md animate-in fade-in-0 zoom-in-95"
    >
      {tooltip.text}
    </div>,
    document.body
  );
};
